import React, { useState } from 'react';

import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { LinkProps } from 'src/components/ui/Link';
import Link from 'src/components/ui/Link';
import PropertyCard from 'src/components/ad/PropertyCard';
import Typography from 'src/components/ui/Typography';
import { Tabs, Tab } from 'src/components/ui/Tabs';
import HorizontalScroller, { SnapItem } from 'src/components/ui/HorizontalScroller';

import type { Ad, UserProfileQuery } from 'src/types/__generated__/graphql';
import { AdStatuses } from 'src/types/__generated__/graphql';
import theme from 'src/utils/theme';
import { cardHeightInRemForAds } from 'src/constants/misc';

import { useUserAdsQuery } from 'src/hooks/__generated__/queries';
import { routePathUser } from 'src/constants/router';
import { styledComponent } from 'src/utils/styled';
import useBreakpoints from 'src/hooks/useBreakpoints';

const StyledCardContent = styledComponent(CardContent)`
    position: relative;
    padding: 1.5rem 0;
`;

const StyledLink = styledComponent((props: LinkProps) => <Link {...props} />)`
    color: ${theme.palette.primary.main};
    font-size: 0.875rem;
    font-weight: bold;
`;

type TabTypes = AdStatuses.Available | AdStatuses.Ended;

export interface Props {
    userProfile: NonNullable<UserProfileQuery['userProfile']>;
}

const defaultLimit = 5;

const AdsSection: React.FC<Props> = ({ userProfile }) => {
    const { t } = useTranslation(['profile']);

    const [selectedTab, setSelectedTab] = useState<TabTypes>(AdStatuses.Available);

    const { isMobile } = useBreakpoints();

    const {
        previousData: userAdsPreviousData,
        data: userAds = userAdsPreviousData,
        fetchMore: fetchMoreUserAds,
        loading: loadingUserAds,
    } = useUserAdsQuery({
        variables: {
            userId: String(userProfile.id),
            offset: 0,
            limit: defaultLimit,
            statuses: [selectedTab],
        },
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
    });

    const handleTabChange = (_ev: React.SyntheticEvent, tab: TabTypes) => {
        setSelectedTab(tab);
    };

    const ads = userAds?.userAds ?? [];
    const adsTotalLength =
        selectedTab === AdStatuses.Available ? userProfile.numberOfAvailableAds : userProfile.numberOfEndedAds;
    const cardWidthInRem = 18;
    return (
        <Card elevation={0}>
            <StyledCardContent>
                <Typography variant="h2" serif>
                    {t('profile:myShowcase')}
                </Typography>
                <Box display="flex" alignItems="center" my={2}>
                    <Tabs value={selectedTab} onChange={handleTabChange}>
                        <Tab
                            label={
                                isMobile
                                    ? t('profile:availableAds')
                                    : t('profile:availableAdsCount', {
                                          count: userProfile.numberOfAvailableAds,
                                      })
                            }
                            value={AdStatuses.Available}
                        />
                        <Tab
                            label={
                                isMobile
                                    ? t('profile:endedAds')
                                    : t('profile:endedAdsCount', {
                                          count: userProfile.numberOfEndedAds,
                                      })
                            }
                            value={AdStatuses.Ended}
                        />
                    </Tabs>
                </Box>
                <HorizontalScroller
                    key={`${selectedTab}-HorizontalScroller`}
                    actualCardsLength={ads.length}
                    totalCardsLength={adsTotalLength}
                    cardHeightInRem={cardHeightInRemForAds}
                    cardWidthInPx={cardWidthInRem * 16} // 18rem * 16
                    loading={loadingUserAds}
                    marginBottom={2}
                    handleFetchMore={() =>
                        fetchMoreUserAds({
                            variables: {
                                offset: ads.length,
                                statuses: [selectedTab],
                            },
                        })
                    }
                >
                    {ads.map((ad) => (
                        <SnapItem key={ad.id}>
                            <Box width={`${cardWidthInRem}rem`} sx={{ height: '16.6rem' }}>
                                <PropertyCard ad={ad as Ad} showTags isDarkCard />
                            </Box>
                        </SnapItem>
                    ))}
                </HorizontalScroller>
                {adsTotalLength > 0 && (
                    <Box textAlign={isMobile ? 'center' : 'right'}>
                        <StyledLink
                            href={{
                                pathname: `${routePathUser}/${userProfile.id}/ads`,
                                query: { tab: selectedTab },
                            }}
                        >
                            {t('profile:seeAllShowcase')}
                        </StyledLink>
                    </Box>
                )}
            </StyledCardContent>
        </Card>
    );
};

export default AdsSection;
